import { z } from "zod";
import { CustomerSmsSchema } from "./messaging";

export const AuditEventSchema = z.object({
  action: z.string().min(1), // e.g. "tenant.create", "shift.claim.approve"
  actorUid: z.string(),
  actorKind: z.enum(["member", "superAdmin", "system", "customer"]),
  tenantId: z.string().nullable().default(null),
  targetPath: z.string().nullable().default(null),
  details: z.record(z.unknown()).default({}),
  at: z.number(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const CustomerSchema = z.object({
  displayName: z.string().min(1),
  company: z.string().default(""),
  email: z.string().default(""),
  phone: z.string().default(""),
  addressLine1: z.string().default(""),
  city: z.string().default(""),
  state: z.string().default(""),
  zip: z.string().default(""),
  lat: z.number().nullable().default(null),
  lng: z.number().nullable().default(null),
  tags: z.array(z.string()).default([]),
  note: z.string().default(""),
  /** Where they came from: one of the company's own lead sources (Setup → Lead sources). */
  leadSource: z.string().max(80).optional(),
  /** One of the company's customer types (Homeowner, Builder, Commercial, or their own); unset = read from company/projects (customerTypeOf). */
  type: z.string().max(40).optional(),
  /** Mailchimp: when they last went to the audience (functions write it). */
  mailchimp: z.object({ at: z.number(), audienceId: z.string(), status: z.string().default("") }).optional(),
  /** Clevis Relay: their referral code (made on first use), who sent them, and when we last asked them to refer. */
  referralCode: z.string().max(12).optional(),
  referredBy: z.object({ referralId: z.string(), customerId: z.string(), name: z.string().default("") }).optional(),
  referralAskedAt: z.number().optional(),
  referralCampaignId: z.string().optional(),
  archivedAt: z.number().nullable().default(null),
  // Texting consent and opt-out (STOP); see schemas/messaging.ts.
  sms: CustomerSmsSchema.optional(),
  // Builder cleanup: this duplicate became project mergedProjectId under mergedInto.
  mergedInto: z.string().optional(),
  mergedProjectId: z.string().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Customer = z.infer<typeof CustomerSchema>;

/** The company's own customer types (Customers → "Customer types…"); names are the ids, like lead sources. */
export const DEFAULT_CUSTOMER_TYPES = ["Homeowner", "Builder", "Commercial"];
export const CustomerSettingsSchema = z.object({
  types: z.array(z.string().trim().min(1).max(40)).min(1).max(20).default(DEFAULT_CUSTOMER_TYPES),
  updatedAt: z.number().optional(),
});
export type CustomerSettings = z.infer<typeof CustomerSettingsSchema>;
/** A customer's type: what the office set; else a builder when they own a project, a business when they have a company name, else a homeowner. */
export function customerTypeOf(c: { type?: string; company?: string }, ownsProject: boolean): string {
  if (c.type && c.type.trim()) return c.type.trim();
  if (ownsProject) return "Builder";
  if ((c.company ?? "").trim()) return "Commercial";
  return "Homeowner";
}

// Deliberately few default statuses — custom workflows come later, and never
// as a horizontally scrolling pill strip. Pipeline buckets (partially
// complete, billed, paid…) are DERIVED from jobs + children + invoices,
// never stored — see apps/app/src/lib/pipeline.ts.
export const JobStatusSchema = z.enum(["scheduled", "in_progress", "done", "canceled"]);
export type JobStatus = z.infer<typeof JobStatusSchema>;

// A job is usually standalone work; visits/job-walks/warranty claims are
// child jobs linked by parentJobId — they schedule, route and clock like any
// job, and their story lands on the parent's activity timeline.
export const JobKindSchema = z.enum(["work", "visit", "job_walk", "warranty"]);
export type JobKind = z.infer<typeof JobKindSchema>;

// Warranty claims: who the work is on behalf of. Site contact stays
// customerId; the payer is billToCustomerId when they differ.
export const CoverageSchema = z.enum(["builder", "manufacturer", "homeowner", "goodwill"]);
export type Coverage = z.infer<typeof CoverageSchema>;

export const JobSchema = z.object({
  title: z.string().min(1),
  // One of the company's job types (Service, Warranty, …) when picked.
  jobType: z.string().optional(),
  customerId: z.string().nullable().default(null),
  // customerId's display name, copied onto the job so lists and search never
  // wait on loading every customer (CSG has ~13k). Writers may set it; the
  // server keeps it right: onJobWritten fills or corrects it, and a customer
  // rename updates its jobs.
  customerName: z.string().optional(),
  locationId: z.string().nullable().default(null),
  status: JobStatusSchema.default("scheduled"),
  kind: JobKindSchema.default("work"),
  parentJobId: z.string().nullable().default(null),
  // Optional dispatch layer: intake routes the job to a department; that
  // department's manager assigns techs. Small companies never set it.
  departmentId: z.string().nullable().default(null),
  projectId: z.string().nullable().default(null),
  // The lot within the project ("Lot 26"), until Google has the street address.
  lot: z.string().max(80).optional(),
  /** The lot record (projects/{projectId}/lots/{lotId}) this job is for. */
  lotId: z.string().nullable().optional(),
  // The project item this job builds ("Elevation A"), copied when picked so the
  // job keeps the bid scope and prices even if the item changes later.
  // How this call pays the tech: by the hour (time clock) or piece rate.
  // Set when booked (defaults from the job type); the weekly report reads it.
  payMode: z.enum(["hourly", "piece"]).optional(),
  projectItem: z
    .object({
      id: z.string(),
      name: z.string(),
      lines: z.array(z.object({ description: z.string(), quantity: z.number(), unitPriceCents: z.number() })).max(100),
    })
    .optional(),
  billToCustomerId: z.string().nullable().default(null),
  coverage: CoverageSchema.nullable().default(null),
  startAt: z.number().nullable().default(null),
  endAt: z.number().nullable().default(null),
  assignedUids: z.array(z.string()).default([]),
  address: z.string().default(""),
  lat: z.number().nullable().default(null),
  lng: z.number().nullable().default(null),
  note: z.string().default(""),
  // Builder PO / order reference (work-order and email intake) — the dedupe
  // handle: one PO number should never become two jobs.
  poNumber: z.string().nullable().default(null),
  // Routing (written by the optimizer / geocoder, never by clients):
  sequence: z.number().int().nullable().default(null),
  plannedEtaAt: z.number().nullable().default(null),
  serviceMinutes: z.number().int().min(1).max(600).nullable().default(null),
  geocodeTriedAt: z.number().nullable().default(null),
  doneAt: z.number().nullable().default(null),
  // The customer's completion sign-off (the signature itself lives in jobs/{id}/signoffs).
  signedOffAt: z.number().nullable().default(null),
  signedOffBy: z.string().nullable().default(null),
  /** The customer's OK before the visit, when the job type asks for one (never a gate). */
  customerApproval: z.object({ at: z.number(), byName: z.string().default(""), via: z.enum(["portal", "office"]) }).nullable().optional(),
  createdByUid: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  // Who made the latest edit from the app (rules: only ever yourself). The job
  // log attributes a change to them when `at` matches the write's updatedAt.
  editedBy: z.object({ uid: z.string(), name: z.string(), at: z.number() }).optional(),
  // Set by a builder-cleanup run (or its undo) that moved this job; such writes skip the trigger's work.
  migratedBy: z.string().optional(),
  // Who FieldPulse had on this job. Someone not in Clevis yet is assigned the
  // moment they join (setMembership matches their email).
  fpAssignees: z.array(z.object({ fpUserId: z.number(), email: z.string(), name: z.string() })).optional(),
  fpAssigneeEmails: z.array(z.string()).optional(),
  // Requested vs. done: `note` is what was asked for; `findings` is what the
  // tech actually found ("broken spring, not the track"), and the work items
  // (jobs/{id}/workItems) are what was done and used. The server keeps the
  // billable subtotal here so visits roll up to their parent job cheaply.
  findings: z.string().max(5000).optional(),
  // Customer texting: the public pages for this job (customerLinks tokens),
  // when a tech said they're on the way, and a per-job "don't text" switch.
  textLinks: z.object({ tech: z.string().optional(), review: z.string().optional() }).optional(),
  enRouteAt: z.number().optional(),
  /** When the tech pressed "I'm here" (the arrival prompts count photos and notes from here). */
  arrivedAt: z.number().nullable().optional(),
  /** Times the crew showed up and couldn't do the work (newest last). */
  dryRuns: z.array(z.record(z.string(), z.unknown())).optional(),
  /** "Partial complete": done for today with a return visit opened. */
  partialAt: z.number().nullable().optional(),
  /** What the job walk (a child job) found, copied here when it finishes: the estimate starts from it. */
  jobWalk: z
    .object({
      jobId: z.string(),
      doneAt: z.number(),
      byName: z.string().default(""),
      findings: z.string().default(""),
      notes: z.array(z.string()).max(20).default([]),
      photos: z.number().int().nonnegative().default(0),
    })
    .optional(),
  enRouteByUid: z.string().optional(),
  noCustomerTexts: z.boolean().optional(),
  workTotalCents: z.number().int().optional(),
  workCount: z.number().int().optional(),
});
export type Job = z.infer<typeof JobSchema>;

// A customer's sign-off on finished work, signed on the tech's device.
// Server-written; read by the office and the job's assigned techs.
export const JobSignoffSchema = z.object({
  signerName: z.string().min(2).max(200),
  signaturePngBase64: z.string().max(400000),
  note: z.string().max(1000).default(""),
  byUid: z.string(),
  byName: z.string().default(""),
  at: z.number(),
});
export type JobSignoff = z.infer<typeof JobSignoffSchema>;

// Outbound customer notifications (settings/notifications). Written by
// functions only; admins flip them on per company — all off by default.
export const NotificationSettingsSchema = z.object({
  // Email the job's payer (bill-to, else customer) when a job is marked done.
  completionEmailEnabled: z.boolean().default(false),
  // Optional copy to the office or a billing inbox.
  completionEmailCc: z.string().email().or(z.literal("")).default(""),
  updatedAt: z.number(),
});
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

// Projects: the engagement container — "Hilton Hotel", "Crestline release,
// 30 lots". Many jobs across many sites roll up here. Entirely optional:
// a simple service call never touches one.
export const ProjectSchema = z.object({
  name: z.string().min(1).max(160),
  customerId: z.string().nullable().default(null), // the builder / GC
  status: z.enum(["active", "closed"]).default("active"),
  note: z.string().max(5000).default(""),
  // Where the development is: an address once there is one, a dropped pin before.
  address: z.string().max(300).optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  /** We're a sub here (not hired by the owner): track Nevada Notices of Right to Lien per lot. */
  lienNotices: z.boolean().optional(),
  // Builder cleanup: the duplicate customer this project was made from (undo uses it).
  mergedFromCustomerId: z.string().optional(),
  fpCustomerId: z.number().optional(),
  /** QuickBooks files this development as the builder's sub-customer "Builder:qbName"; blank = the name. */
  qbName: z.string().max(41).optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

// One thing done or used on a job or visit: a part (maybe from stock), labor,
// a service. Written by functions only (stock moves with it).
export const JobWorkItemSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().positive().max(100000),
  /** The customer's price (never a cost). */
  unitPriceCents: z.number().int().min(-10_000_000).max(100_000_000),
  /** Bill it (warranty / goodwill work is logged but not billed). */
  billable: z.boolean().default(true),
  itemId: z.string().nullable().default(null),
  /** A kit's parts, as the pricebook had them (stock moves each part). */
  components: z.array(z.object({ itemId: z.string(), name: z.string(), quantity: z.number() })).nullable().default(null),
  /** Taken from stock when logged (inventory already moved). */
  fromStock: z.boolean().default(false),
  /** Of fromStock, how much came off the truck (per item: a kit lists its parts). */
  fromTruck: z.array(z.object({ itemId: z.string(), quantity: z.number() })).optional(),
  /** Piece pay on a tech-added part, when the company wants a manager's OK first (tenant.pieceExtras). */
  piece: z
    .object({
      status: z.enum(["pending", "approved", "declined"]),
      byUid: z.string().nullable().default(null),
      byName: z.string().default(""),
      at: z.number().nullable().default(null),
    })
    .optional(),
  addedByUid: z.string(),
  addedByName: z.string().default(""),
  at: z.number(),
});
export type JobWorkItem = z.infer<typeof JobWorkItemSchema>;

/** A plain priced line (what the customer is charged; never a cost). */
export const PricedLineSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().positive().max(100000),
  unitPriceCents: z.number().int().min(-10_000_000).max(100_000_000),
});
export type PricedLine = z.infer<typeof PricedLineSchema>;

// A project's bid option, e.g. KB Homes · Desert Skies → "Elevation A": bid,
// approved, then picked on every lot's work order or job, so the job carries
// its scope and the invoice starts from its prices.
export const ProjectItemSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(5000).default(""),
  lines: z.array(PricedLineSchema).max(100).default([]),
  totalCents: z.number().int().default(0),
  status: z.enum(["bid", "awarded", "archived"]).default("bid"),
  /** What a tech earns for building one of these on a piece-rate job (e.g. per lot). */
  pieceRateCents: z.number().int().nonnegative().optional(),
  /** The approved estimate it came from, when there is one. */
  estimateId: z.string().nullable().default(null),
  createdByUid: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type ProjectItem = z.infer<typeof ProjectItemSchema>;

// A lot in a builder's development: Customer → Project → Lot → Jobs. Optional:
// homeowner and commercial work has no lots. Its jobs (install, punch, warranty)
// link by lotId; "key" ("26" for "Lot 0026") finds it from a work order.
export const LotSchema = z.object({
  name: z.string().trim().min(1).max(80),
  key: z.string().min(1).max(80),
  address: z.string().max(300).default(""),
  /** Where it is on the map: found from the address, or a dropped pin before there is one. */
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  /** The plan / elevation going on this lot. */
  projectItemId: z.string().nullable().default(null),
  projectItemName: z.string().max(120).default(""),
  /** The homeowner once the house sells (who gets warranty visits and texts). */
  homeownerName: z.string().max(120).default(""),
  homeownerPhone: z.string().max(40).default(""),
  homeownerEmail: z.string().max(200).default(""),
  /** The homeowner's texting state (STOP / START) when texts go to them. Functions write it. */
  sms: CustomerSmsSchema.optional(),
  note: z.string().max(2000).default(""),
  status: z.enum(["open", "closed"]).default("open"),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Lot = z.infer<typeof LotSchema>;

/** "Lot 0026", "lot #26", "26" → "26"; "587/2" stays; letters keep (26A → "26a"). */
export function lotKey(label: string): string {
  const s = label.trim().toLowerCase().replace(/^lot\s*#?\s*/, "").replace(/\s+/g, " ").trim();
  return s.replace(/^0+(?=\d)/, "");
}
/** How a lot is named: "26" → "Lot 26"; anything that already says what it is stays. */
export function lotName(label: string): string {
  const t = label.trim().replace(/\s+/g, " ");
  if (!t) return "";
  return /^lot\b/i.test(t) || /^[0-9][0-9a-z/-]*$/i.test(t) ? `Lot ${lotKey(t).toUpperCase()}` : t;
}
/** "1-40", "1-40, 52, 60A" → the lot labels to add, one per lot (at most 500). */
export function expandLotRange(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean)) {
    const m = /^(?:lot\s*#?\s*)?(\d+)\s*(?:-|–|to)\s*(?:lot\s*#?\s*)?(\d+)$/i.exec(part);
    if (m) {
      const a = Number(m[1]);
      const b = Number(m[2]);
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      for (let n = lo; n <= hi && out.length < 500; n++) out.push(String(n));
    } else if (out.length < 500) out.push(part);
  }
  return [...new Map(out.map((l) => [lotKey(l), l])).values()].filter((l) => lotKey(l));
}
export type Project = z.infer<typeof ProjectSchema>;

// Departments: the optional queue layer. v1 carries managers; member rosters
// arrive with the schedule Depts tab.
export const DepartmentSchema = z.object({
  name: z.string().min(1).max(80),
  /** "group": a cross-department team (Operations, Sales, Management). Groups
   * filter people; only departments get jobs and requests routed to them. */
  kind: z.enum(["department", "group"]).default("department"),
  managerUids: z.array(z.string()).default([]),
  memberUids: z.array(z.string()).default([]),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Department = z.infer<typeof DepartmentSchema>;

// Staged procurement: a planned part waits on its trigger — a calendar date
// (absolute, or offset from the job's scheduled start) or the arrival of other
// parts on the same job — then flips to "ready" for purchasing to order.
export const OrderTriggerSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("date"),
    at: z.number().nullable().default(null),
    offsetDays: z.number().int().min(0).max(730).nullable().default(null),
  }),
  z.object({
    kind: z.literal("afterItems"),
    triggerItemIds: z.array(z.string()).min(1).max(50),
  }),
]);
export type OrderTrigger = z.infer<typeof OrderTriggerSchema>;

// A part on order for a job. tenantId/jobId are denormalized onto the doc so
// collection-group queries ("every open order in my company") can be secured
// and filtered. Lifecycle: planned → ready → ordered → received; only
// "ordered" parks the job in the Waiting-on-parts pipeline bucket.
export const OrderedItemSchema = z.object({
  tenantId: z.string(),
  jobId: z.string(),
  description: z.string().min(1).max(300),
  vendor: z.string().max(120).default(""),
  vendorId: z.string().nullable().default(null),
  // Linked pricebook item: receiving then increments its on-hand count.
  itemId: z.string().nullable().default(null),
  quantity: z.number().positive().max(100000).default(1),
  expectedAt: z.number().nullable().default(null),
  status: z.enum(["planned", "ready", "ordered", "received"]).default("ordered"),
  trigger: OrderTriggerSchema.nullable().default(null),
  readyAt: z.number().nullable().default(null),
  orderedByUid: z.string(),
  orderedAt: z.number(),
  receivedAt: z.number().nullable().default(null),
  /** The purchase order it's on (set when the PO is placed). */
  purchaseOrderNumber: z.number().int().optional(),
  /** Set when receiving a PO already put it into stock (the "received" flip mustn't again). */
  stockMovedBy: z.string().optional(),
});
export type OrderedItem = z.infer<typeof OrderedItemSchema>;
